import {
  Box,
  ExpandableSection,
  Icon,
  IconProps,
  StatusIndicator,
} from "@amzn/awsui-components-console";
import React, { useState } from "react";

import { ProcessingStepSection as Section } from "@/types/chat-response";

import { ProcessingStepRender } from "../chat/types";

export interface ProcessingStepSectionProps
  extends Partial<Omit<ProcessingStepRender, "section">> {
  section: ProcessingStepRender["section"];
  children?: React.ReactNode;
  /** Elapsed time in seconds since this step started (until next step or now). */
  elapsedSeconds?: number;
  /** Whether this is the currently active (most recent) step. */
  isActive?: boolean;
  /** Whether the section should start expanded. */
  defaultExpanded?: boolean;
}

/** Strip emoji characters from a string for cleaner display. */
const stripEmoji = (text: string): string =>
  text
    .replace(
      /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{27BF}\u{2B50}\u{2B55}\u{231A}-\u{23F3}\u{23F8}-\u{23FA}\u{25AA}-\u{25FE}\u{2934}-\u{2935}\u{2190}-\u{21FF}]/gu,
      ""
    )
    // Variation selector, ZWJ, and keycap combining marks are stripped via
    // alternation (not a character class) so they don't form misleading
    // base+combining sequences.
    .replace(/\u{FE0F}|\u{200D}|\u{20E3}/gu, "")
    .replace(/^\s+/, ""); // trim leading whitespace left after emoji removal

/** Map section type to a Cloudscape Icon name for the section header. */
const getSectionIcon = (section: Section | string): IconProps.Name => {
  // Handle dynamic "Tool: ..." section names
  if (typeof section === "string" && section.startsWith("Tool:")) {
    return "script";
  }

  switch (section) {
    case Section.AGENT_PROGRESS:
    case Section.AGENT_PROCESSING:
    case Section.PROCESSING:
      return "contact";
    case Section.TOOL_PROGRESS:
    case Section.TOOL_EXECUTION_DETAILS:
      return "script";
    case Section.SAFETY_CHECK:
    case Section.RESPONSE_SAFETY:
      return "security";
    case Section.CACHE:
      return "multiscreen";
    case Section.DATABASE:
    case Section.DATABASE_QUERY:
      return "search";
    case Section.FINAL_RESPONSE:
      return "status-positive";
    case Section.TRACING:
    case Section.TRACE_DETAILS:
      return "status-info";
    case Section.METRICS:
      return "status-info";
    default:
      return "contact";
  }
};

/** Format elapsed seconds into a human-readable string. */
const formatElapsed = (seconds: number): string => {
  if (seconds < 1) return "<1s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

const ProcessingStepSection: React.FC<ProcessingStepSectionProps> = ({
  section,
  messages,
  children,
  elapsedSeconds,
  isActive,
  defaultExpanded,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const iconName = getSectionIcon(section);

  // For "Agent Progress" sections, show a preview of the first message
  const sectionLabel = (() => {
    if (
      section === "Agent Progress" &&
      messages &&
      messages.length > 0
    ) {
      const firstMsg = stripEmoji(messages[0]);
      if (firstMsg && firstMsg.length > 0) {
        const preview =
          firstMsg.length > 30 ? firstMsg.slice(0, 30) + "..." : firstMsg;
        return `Agent: ${preview}`;
      }
    }
    return section;
  })();

  // Build the header content: icon + section name + elapsed timer
  const headerContent = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "flex-start",
        gap: "8px",
        width: "100%",
      }}
    >
      <Icon name={iconName} size="small" />
      <span style={{ whiteSpace: "normal", overflow: "hidden", minWidth: 0, flex: 1 }}>
        {sectionLabel}
      </span>
      {elapsedSeconds != null && (
        <span
          style={{
            marginLeft: "auto",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <Box
            color="text-status-inactive"
            display="inline"
            fontSize="body-s"
          >
            {isActive ? (
              <StatusIndicator type="in-progress">
                {formatElapsed(elapsedSeconds)}
              </StatusIndicator>
            ) : (
              <span>{formatElapsed(elapsedSeconds)}</span>
            )}
          </Box>
        </span>
      )}
    </span>
  );

  // Determine if there's content to show
  const hasContent = children || (messages && messages.length > 0);

  if (!hasContent) {
    // No expandable content — just render the header as a static row
    return (
      <Box padding={{ vertical: "xxxs", horizontal: "xs" }}>
        {headerContent}
      </Box>
    );
  }

  return (
    <ExpandableSection
      variant="footer"
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      headerText={headerContent}
    >
      {children
        ? children
        : messages?.map((item, index) => {
            const cleanText = stripEmoji(item);
            if (!cleanText) return null;
            return (
              <Box
                key={`${section}-item-${index}`}
                padding={{ left: "s" }}
                margin={{ top: "xxxs" }}
                color="text-body-secondary"
                fontSize="body-s"
              >
                {cleanText}
              </Box>
            );
          })}
    </ExpandableSection>
  );
};

export default ProcessingStepSection;
